import type { DividerProps } from '@hover/blueprint';
import {
  Body,
  Box,
  Button,
  DesignSystemProvider,
  Icon,
  IconButton,
  Link,
  Sheet,
  Divider as SystemDivider,
  useDisclosure,
} from '@hover/blueprint';
import {
  iconChevronRight,
  iconClose,
  iconMenu,
  iconPlus,
  iconUser,
} from '@hover/icons';
import React from 'react';
import { useLocation, useParams } from 'react-router';

import { useFeatureAccesses } from 'src/modules/propertyDetails/hooks/useFeatureAccesses';
import { useHasAccessForOrg } from 'src/modules/propertyDetails/hooks/useHasAccessForOrg';
import { useIsHoverAdmin } from 'src/modules/propertyDetails/hooks/useIsHoverAdmin';
import { SheetNew } from 'src/shared/components/NavBar/SheetNew';
import { StyledNavBar } from 'src/shared/components/NavBar/StyledNavBar';
import { FeatureFlag } from 'src/shared/constants';
import type { User } from 'src/shared/graphql/generated';
import {
  AclTemplateOptions,
  useProfileQuery,
} from 'src/shared/graphql/generated';
import {
  TranslationNamespace,
  useCurrentPage,
  useTranslate,
  useTreatment,
} from 'src/shared/hooks';
import { External, Route } from 'src/shared/route';
import { isFlagEnabled } from 'src/shared/telemetry';
import { getInitialsFromName } from 'src/shared/utils/initials';
import { SHEET_SPACING_RESET } from 'src/shared/utils/sheetSpacingReset';

import { NavLink } from './NavLink';
import { SearchInput } from './SearchInput';

interface NavLinkData {
  active?: boolean;
  href: string;
  label: string;
  userHasAccess: boolean;
}

const NavMenuItem: React.FC<{ path: string; display: string }> = ({
  path,
  display,
  ...props
}) => (
  <NavLink
    display="block"
    href={path}
    margin={0}
    paddingY={400}
    width="100%"
    {...props}
  >
    {display}
  </NavLink>
);

export const MenuLinkDivider = (props: DividerProps) => (
  <SystemDivider borderColor="neutral.200" margin="0" size="small" {...props} />
);

const useUserAccess = ({ user }: { user?: User }) => {
  const [isTeamAdmin] = useHasAccessForOrg([AclTemplateOptions.Admin]);
  const [isJobManager] = useHasAccessForOrg([AclTemplateOptions.JobManager]);

  const isHomeowner = user?.isHomeowner;
  const canManageEhiProjects = isTeamAdmin || isJobManager;

  const location = useLocation();

  const {
    isUpgradeToProEnabled,
    isEstimationEnabled,
    isMaterialListEnabled,
    isInspectionChecklistEnabled,
    isXactimateEnabled,
    isAnalyticsPageEnabled,
  } = useFeatureAccesses();

  const showWorkflows =
    (isMaterialListEnabled ||
      ((isEstimationEnabled || isInspectionChecklistEnabled) &&
        canManageEhiProjects)) &&
    !isXactimateEnabled;

  const isWebUploadEnabled = isFlagEnabled(
    useTreatment(FeatureFlag.Web.WEB_UPLOAD),
  );

  const currentPage = useCurrentPage();

  const isPropertiesPage = currentPage === Route.PROPERTIES.page;
  const isEhiPage = location.pathname.includes('/ehi/#/');
  const isManageTeamsInvite = location.pathname.includes('manage-teams/invite');

  const shouldShowSearch =
    currentPage !== Route.PROPERTY_DETAILS.page &&
    currentPage !== Route.ORG_ASSIGNMENTS.page &&
    currentPage !== Route.USER_ASSIGNMENTS.page &&
    !isEhiPage &&
    !isManageTeamsInvite;

  const isHoverAdmin = useIsHoverAdmin();

  const shouldRenderNewButton = isWebUploadEnabled && isPropertiesPage;

  return {
    showWorkflows,
    isEstimationEnabled,
    isMaterialListEnabled,
    isInspectionChecklistEnabled,
    isXactimateEnabled,
    isUpgradeToProEnabled: isUpgradeToProEnabled && isHomeowner,
    isHomeowner,
    canManageEhiProjects,
    isJobManager,
    isTeamAdmin,
    isHoverAdmin,
    isAnalyticsPageEnabled: isAnalyticsPageEnabled && canManageEhiProjects,
    isWebUploadEnabled,
    shouldShowSearch,
    shouldRenderNewButton,
    shouldSeeScheduleDemo: !isHomeowner,
  };
};

const useGetNavLinks = ({ user }: { user?: User }) => {
  const userAccess = useUserAccess({ user });
  const t = useTranslate(TranslationNamespace.SHARED);
  const location = useLocation();

  const navLinks = [
    {
      href: Route.PROPERTIES.path,
      label: t('navigation.Jobs'),
      userHasAccess: true,
    },
    {
      href: Route.MANAGE_TEAMS.path,
      label: t('navigation.manageTeam'),
      userHasAccess: userAccess.isTeamAdmin,
    },
    {
      href: External.PROJECT_LIST.path,
      label: t('navigation.Estimates'),
      userHasAccess: userAccess.isEstimationEnabled,
    },
    {
      href: External.ANALYTICS.path,
      label: t('navigation.analytics'),
      userHasAccess: userAccess.isAnalyticsPageEnabled,
    },
    {
      href: External.ADMIN.path,
      label: t('navigation.admin'),
      userHasAccess: userAccess.isHoverAdmin,
    },
  ] as NavLinkData[];

  return navLinks
    .map(link => {
      link.active = location.pathname === link.href;
      return link;
    })
    .filter(link => link.userHasAccess);
};

const NavigationBar = ({ onLogout }: { onLogout: () => void }) => {
  const {
    isOpen: isSheetNewOpen,
    onClose: onSheetNewClose,
    onOpen: onSheetNewOpen,
  } = useDisclosure();

  const {
    isOpen: isProfileSheetOpen,
    onClose: onProfileSheetClose,
    onOpen: onProfileSheetOpen,
  } = useDisclosure();

  const t = useTranslate(TranslationNamespace.SHARED);
  const { data } = useProfileQuery();
  const user = data?.profile?.user as User;
  const userAccess = useUserAccess({ user });
  const navLinks = useGetNavLinks({ user });

  const { groupId } = useParams<{ groupId: string }>();
  const isGroupMode = !!groupId;

  const scheduleDemoLink = new URL(
    'https://hover.to/lp/hover-sdr/web-app-demo',
  );
  const demoParams = {
    utm_source: '7014N000001zRNkQAM',
    firstname: data?.profile?.user?.firstName ?? '',
    lastname: data?.profile?.user?.lastName ?? '',
    email: data?.profile?.user?.email ?? '',
    mobilephone: data?.profile?.user?.mobilePhone ?? '',
    company: data?.profile?.user?.orgs?.[0].name ?? '',
  };
  Object.entries(demoParams).forEach(([key, value]) => {
    scheduleDemoLink.searchParams.append(key, value);
  });

  if (isGroupMode) return null;

  const org = user?.orgs[0];

  const walletBalance = org?.wallet?.totalBalance
    ? org?.wallet?.totalBalance / 100.0
    : 0;

  if (!user) {
    return <StyledNavBar />;
  }

  return (
    <DesignSystemProvider attach>
      <Box
        backgroundColor="neutral.0"
        position="sticky"
        top={0}
        width="100%"
        zIndex={1}
      >
        <Box as="header" width="100%" zIndex={1}>
          <StyledNavBar>
            <Box display={{ base: 'none', width350: 'inherit' }}>
              {navLinks.map(link => (
                <NavLink active={link.active} href={link.href} key={link.label}>
                  {link.label}
                </NavLink>
              ))}
            </Box>

            <Box
              alignItems="center"
              flex={1}
              gap={200}
              justifyContent="flex-end"
            >
              {userAccess.shouldShowSearch && (
                <SearchInput
                  display={{
                    base: 'none',
                    tablet: 'block',
                  }}
                  isToggleable
                  maxWidth="750"
                  width="100%"
                />
              )}
              {userAccess.shouldSeeScheduleDemo && (
                <Button
                  as="a"
                  display={{ base: 'none', desktop: 'inherit' }}
                  href={scheduleDemoLink.toString()}
                  variant={{ base: 'ghost', width350: 'glass' }}
                >
                  {t('navigation.ScheduleDemo')}
                </Button>
              )}
              {userAccess.shouldRenderNewButton && (
                <>
                  <Button
                    display={{ base: 'none', width350: 'block' }}
                    iconBefore={iconPlus}
                    onClick={onSheetNewOpen}
                    variant="glass"
                  >
                    {t('navigation.new')}
                  </Button>
                  <IconButton
                    display={{ base: 'block', width350: 'none' }}
                    icon={iconPlus}
                    label={t('navigation.new')}
                    onClick={onSheetNewOpen}
                    variant="ghost"
                  />
                </>
              )}

              <Button
                display={{ base: 'none', width350: 'inherit' }}
                onClick={onProfileSheetOpen}
                variant={{ base: 'ghost', width350: 'glass' }}
                width="375"
              >
                {user?.name?.length ? (
                  getInitialsFromName(user.name)
                ) : (
                  <Icon icon={iconUser} size="medium" />
                )}
              </Button>
              <IconButton
                display={{ base: 'inherit', width350: 'none' }}
                icon={iconMenu}
                label="menu"
                onClick={onProfileSheetOpen}
                variant={{ base: 'ghost', width350: 'glass' }}
              />
            </Box>
          </StyledNavBar>
        </Box>
        <Sheet
          contentProps={{ height: '100%', ...SHEET_SPACING_RESET }}
          header={
            <Box
              alignItems="center"
              backgroundColor="neutral.50"
              justifyContent="space-between"
              paddingX="800"
              paddingY="700"
            >
              <Box flexDirection="column">
                <Body
                  fontSize="200"
                  fontWeight="700"
                  letterSpacing="normal"
                  margin="0"
                  overflow="hidden"
                  sx={{
                    '-webkit-font-smoothing': 'antialiased',
                  }}
                  textOverflow="ellipsis"
                >
                  {user?.email}
                </Body>
                {userAccess.isTeamAdmin && (
                  <Body color="text.secondary" marginY="0" textStyle="body300">
                    {t('currency', { val: walletBalance })}&nbsp;
                    {t('navigation.credit')}
                  </Body>
                )}
              </Box>
              <IconButton
                icon={iconClose}
                label="close"
                onClick={onProfileSheetClose}
                variant="ghost"
              />
            </Box>
          }
          isClosable={false}
          isOpen={isProfileSheetOpen}
          maxContentHeight="80vh"
          onClose={onProfileSheetClose}
          size="full"
        >
          <Box
            flexDirection="column"
            height="100%"
            justifyContent="space-between"
            paddingX="800"
          >
            <Box flexDirection="column" marginBottom="700">
              {navLinks.map((link, index) => {
                const isLast = index === navLinks.length - 1;
                return (
                  <Box
                    alignItems="center"
                    display={{ base: 'block', width350: 'none' }}
                    key={link.label}
                    marginBottom={isLast ? 700 : 0}
                  >
                    <Box marginY={300}>
                      <NavMenuItem display={link.label} path={link.href} />
                      <Box>
                        <IconButton
                          as="a"
                          href={link.href}
                          icon={iconChevronRight}
                          label=""
                          variant="ghost"
                        />
                      </Box>
                    </Box>
                    {!isLast && <MenuLinkDivider />}
                  </Box>
                );
              })}

              <Box display="block" marginTop={300}>
                {userAccess.showWorkflows && (
                  <NavMenuItem
                    display={t('navigation.manageWorkflows')}
                    path={External.MANAGE_WORKFLOWS.path}
                  />
                )}
                {!userAccess.isHomeowner && (
                  <>
                    <NavMenuItem
                      display={t('navigation.settings')}
                      path={External.SETTINGS.path}
                    />
                  </>
                )}
                {userAccess.isTeamAdmin && (
                  <>
                    <NavMenuItem
                      display={t('navigation.planAndBilling')}
                      path={External.PLAN.path}
                    />
                  </>
                )}
                <Box paddingY={400} width="100%">
                  <Button
                    _hover={{ backgroundColor: 'neutral.0 !important' }}
                    height={150}
                    onClick={onLogout}
                    paddingX={0}
                    paddingY={0}
                    variant="ghost"
                  >
                    {t('logOut')}
                  </Button>
                </Box>
              </Box>
            </Box>
            {userAccess.isUpgradeToProEnabled && (
              <Box
                backgroundColor="neutral.50"
                borderRadius="large"
                marginBottom="700"
                paddingX="300"
                textAlign="center"
              >
                <Box flexDirection="column" margin="500" width="100%">
                  <Body fontWeight="bodyBold" marginBottom="200">
                    {t('navigation.professional')}
                  </Body>
                  <Link
                    display="inline"
                    href={Route.SIGN_UP_PROFESSIONAL_UPGRADE.path}
                    marginBottom={300}
                    marginX={0}
                  >
                    {t('navigation.upgrade')}
                  </Link>
                </Box>
              </Box>
            )}
          </Box>
        </Sheet>
        <SheetNew isOpen={isSheetNewOpen} onClose={onSheetNewClose} />
      </Box>
    </DesignSystemProvider>
  );
};

export { NavigationBar };
